import { useState, useEffect, useCallback, useRef } from "react";

// ── Constants ─────────────────────────────────────────────────────────────────
// Access is controlled by ADMIN_PASSWORD env var in the admin-data Edge Function.
const TIER_COLORS    = { free: "#9B9690", starter: "#B08D57", pro: "#7FB98E", owner: "#163C2F" };
const TIER_LABELS    = { free: "Free", starter: "Starter", pro: "Pro", owner: "Owner ✦" };
const SUB_STATUS_LABELS = { trialing: "Trialing", active: "Active", canceling: "Canceling Soon", past_due: "Past Due", canceled: "Canceled", free: "Free", other: "Other" };
const SUB_STATUS_COLORS = { trialing: "#B08D57", active: "#7FB98E", canceling: "#f59e0b", past_due: "#dc2626", canceled: "#9B9690", free: "#9B9690", other: "#9B9690" };
const SECTIONS = [
  { id: "dashboard",  label: "Dashboard",  group: "Overview" },
  { id: "users",      label: "Users",       group: "Manage" },
  { id: "properties", label: "Properties",  group: "Manage" },
  { id: "subscriptions", label: "Subscriptions", group: "Manage" },
  { id: "waitlist",   label: "Waitlist",    group: "Feedback" },
  { id: "ideas",      label: "Ideas",       group: "Feedback" },
  { id: "error-log",       label: "Error Log",      group: "Platform" },
  { id: "health",          label: "Health",         group: "Platform" },
  { id: "activity",        label: "Activity",       group: "Platform" },
  { id: "report-exports",  label: "Report Exports", group: "Revenue"  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate  = d => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtDateTime = d => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const fmtMoney = n => "$" + Number(n || 0).toLocaleString();
const initials = name => (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

function Badge({ label, color }) {
  return (
    <span style={{ background: color + "22", color, border: `1px solid ${color}44`, padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {label}
    </span>
  );
}
function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, padding: "20px 22px", flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, color: "#9B9690", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 30, color: "#163C2F", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#9B9690", marginTop: 6 }}>{sub}</div>}
    </div>
  );
}
function EmptyState({ icon, title, sub }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px", color: "#9B9690" }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, color: "#2B2B2B", marginBottom: 8 }}>{title}</div>
      {sub && <div style={{ fontSize: 13 }}>{sub}</div>}
    </div>
  );
}

// ── Account Notes (inline in user detail view) ──────────────────────────────
function UserNotes({ userId, callAdmin }) {
  const [notes, setNotes] = useState([]);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    callAdmin("get-notes", { userId }).then(r => setNotes(r?.notes || [])).catch(() => {});
  }, [userId, callAdmin]);

  const add = async () => {
    if (!input.trim()) return;
    setSaving(true);
    await callAdmin("add-note", { userId, note: input.trim() });
    setInput("");
    const r = await callAdmin("get-notes", { userId });
    setNotes(r?.notes || []);
    setSaving(false);
  };

  const del = async (id) => {
    await callAdmin("delete-note", { noteId: id });
    setNotes(prev => prev.filter(n => n.id !== id));
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, padding: "18px 20px", marginTop: 16 }}>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17, color: "#163C2F", marginBottom: 14 }}>Account Notes</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <textarea value={input} onChange={e => setInput(e.target.value)} rows={2} placeholder="Add an internal note…"
          style={{ flex: 1, padding: "9px 12px", fontSize: 13, border: "1px solid #D4CFC7", borderRadius: 8, outline: "none", fontFamily: "'Manrope', sans-serif", resize: "vertical" }} />
        <button onClick={add} disabled={saving || !input.trim()}
          style={{ padding: "8px 16px", background: "#163C2F", color: "#F5F1EA", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Manrope', sans-serif", alignSelf: "flex-end", opacity: saving || !input.trim() ? 0.5 : 1 }}>
          {saving ? "…" : "Add"}
        </button>
      </div>
      {notes.length === 0
        ? <div style={{ fontSize: 13, color: "#9B9690", fontStyle: "italic" }}>No notes yet.</div>
        : notes.map(n => (
          <div key={n.id} style={{ padding: "10px 0", borderTop: "1px solid #F5F1EA", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div>
              <div style={{ fontSize: 13, color: "#2B2B2B", lineHeight: 1.6 }}>{n.note}</div>
              <div style={{ fontSize: 11, color: "#9B9690", marginTop: 4 }}>Added by {n.created_by} · {fmtDate(n.created_at)}</div>
            </div>
            <button onClick={() => del(n.id)} title="Delete"
              style={{ background: "none", border: "none", color: "#9B9690", cursor: "pointer", fontSize: 14, padding: "2px 4px", flexShrink: 0, lineHeight: 1 }}
              onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
              onMouseLeave={e => e.currentTarget.style.color = "#9B9690"}>
              🗑
            </button>
          </div>
        ))
      }
    </div>
  );
}

// ── Admin login screen ────────────────────────────────────────────────────────
function AdminLogin({ onSuccess }) {
  const [pw, setPw]   = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    onSuccess(pw, setErr, setLoading);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#163C2F", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Manrope', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Cormorant+Garamond:wght@400;700&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <div style={{ background: "#fff", borderRadius: 16, padding: "40px 40px", width: "100%", maxWidth: 400, boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <img src="/Leasly-logofinal.png" alt="Leasly" width="32" height="32" style={{ borderRadius: 6, objectFit: "contain" }}/>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: "#163C2F" }}>Leasly</span>
          <span style={{ background: "#B08D5722", color: "#B08D57", border: "1px solid #B08D5744", padding: "2px 8px", borderRadius: 50, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>ADMIN</span>
        </div>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, fontWeight: 400, color: "#2B2B2B", marginBottom: 6 }}>Admin Access</h1>
        <p style={{ fontSize: 13, color: "#9B9690", marginBottom: 24, lineHeight: 1.6 }}>Enter the admin password to continue.</p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <input
            type="password" required value={pw} onChange={e => setPw(e.target.value)}
            autoFocus placeholder="Admin password"
            style={{ padding: "11px 14px", fontSize: 14, border: "1px solid #D4CFC7", borderRadius: 8, outline: "none", fontFamily: "'Manrope', sans-serif" }}
          />
          {err && <div style={{ fontSize: 13, color: "#dc2626", background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px" }}>{err}</div>}
          <button type="submit" disabled={loading} style={{ background: loading ? "#9B9690" : "#163C2F", color: "#F5F1EA", border: "none", borderRadius: 999, padding: "13px 0", fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", cursor: loading ? "not-allowed" : "pointer" }}>
            {loading ? "Verifying…" : "Enter Admin Panel"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
// Access is gated solely by ADMIN_PASSWORD — no Supabase login required.
export default function AdminPanel({ supabase }) {
  // Token lives in React state only — never persisted to storage.
  // Navigating away, refreshing, or closing the tab always clears it.
  const [adminToken, setAdminToken] = useState("");
  // Initialise from URL hash so refreshing lands on the same section.
  const [section, setSection] = useState(() => {
    const hash = window.location.hash.slice(1).toLowerCase();
    const initial = SECTIONS.map(s => s.id).includes(hash) ? hash : "dashboard";
    // replaceState: set the canonical hash without adding an extra history entry
    window.history.replaceState({ adminSection: initial }, "", `/admin#${initial}`);
    return initial;
  });
  const [sidebarOpen,setSidebarOpen]= useState(true);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [search,     setSearch]     = useState("");
  const [viewUser,   setViewUser]   = useState(null);
  const [subStatusFilter, setSubStatusFilter] = useState("");

  const callAdmin = useCallback(async (action, extra = {}) => {
    const { data: res, error: fnErr } = await supabase.functions.invoke("admin-data", {
      body: { action, ...extra },
      headers: { "X-Admin-Token": adminToken },
    });
    let errMsg = fnErr?.message;
    if (fnErr) { try { const b = await fnErr.context?.json?.(); if (b?.error) errMsg = b.error; } catch {} }
    if (errMsg || res?.error) throw new Error(errMsg || res?.error);
    return res;
  }, [supabase, adminToken]);

  const clearToken = useCallback(() => {
    setAdminToken("");
  }, []);

  const loadSection = useCallback(async (sec) => {
    if (!adminToken) return;
    setLoading(true); setError(""); setData(null);
    try {
      const actionMap = { dashboard: "dashboard", users: "dashboard", properties: "dashboard", subscriptions: "subscriptions", waitlist: "waitlist", ideas: "ideas", "error-log": "error-log", health: "health", activity: "activity", "report-exports": "report-exports" };
      const res = await callAdmin(actionMap[sec] || sec);
      setData(res);
    } catch (e) {
      if (e.message?.toLowerCase().includes("unauthorized") || e.message?.includes("401")) {
        clearToken(); // bad token — show password screen again
      } else {
        setError(e.message);
      }
    }
    finally { setLoading(false); }
  }, [adminToken, callAdmin, clearToken]);

  // Navigate to a section: pushState creates a proper history entry so the
  // browser back/forward buttons move between sections without remounting.
  const navigateSection = useCallback((id) => {
    setViewUser(null);
    setSearch("");
    setSubStatusFilter("");
    window.history.pushState({ adminSection: id }, "", `/admin#${id}`);
    setSection(id);
  }, []);

  // Handle browser back / forward via popstate — the component stays mounted
  // so the admin token is preserved; only the section + related UI state update.
  useEffect(() => {
    const onPopState = (e) => {
      const hash = window.location.hash.slice(1).toLowerCase();
      const valid = SECTIONS.map(s => s.id);
      const target = e.state?.adminSection ||
                     (valid.includes(hash) ? hash : "dashboard");
      setSection(target);
      setViewUser(null);  // always close user detail view on navigation
      setSearch("");
      setSubStatusFilter("");
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => { if (adminToken) loadSection(section); }, [section, adminToken]);

  const handleAdminLogin = async (pw, setErr, setLd) => {
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("admin-data", {
        body: { action: "verify" },
        headers: { "X-Admin-Token": pw.trim() },
      });
      let errMsg = fnErr?.message;
      if (fnErr) { try { const b = await fnErr.context?.json?.(); if (b?.error) errMsg = b.error; } catch {} }
      if (errMsg || data?.error) throw new Error(errMsg || data?.error);

      setAdminToken(pw.trim());
    } catch (err) {
      // Surface the real message for rate-limit lockouts; keep the generic,
      // non-revealing message for an actual wrong password.
      const msg = err?.message || "";
      setErr(msg.toLowerCase().includes("too many attempts") ? msg : "Incorrect password. Try again.");
    } finally { setLd(false); }
  };

  // ── Gate: show password screen until verified ──────────────────────────────
  if (!adminToken) return <AdminLogin onSuccess={handleAdminLogin}/>;

  // ── Layout ──────────────────────────────────────────────────────────────────
  const groups = [...new Set(SECTIONS.map(s => s.group))];
  const thStyle = { padding: "10px 14px", textAlign: "left", fontSize: 11, color: "#9B9690", fontFamily: "'Manrope',sans-serif", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, borderBottom: "1px solid #E8E4DC" };
  const tdStyle = { padding: "12px 14px", fontSize: 13, borderBottom: "1px solid #F5F1EA", verticalAlign: "middle" };

  return (
    <div style={{ minHeight: "100vh", background: "#F5F1EA", fontFamily: "'Manrope', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Cormorant+Garamond:wght@400;700&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>

      {/* ── Top nav ── */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 54, background: "#163C2F", display: "flex", alignItems: "center", padding: "0 16px", gap: 12, zIndex: 40, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <button onClick={() => setSidebarOpen(s => !s)} style={{ background: "none", border: "none", color: "rgba(245,241,234,0.6)", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        <img src="/Leasly-logofinal.png" alt="Leasly" width="26" height="26" style={{ borderRadius: 5, objectFit: "contain" }}/>
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 19, fontWeight: 700, color: "#F5F1EA" }}>Leasly</span>
        <span style={{ background: "#B08D5722", color: "#B08D57", border: "1px solid #B08D5744", padding: "2px 8px", borderRadius: 50, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>ADMIN</span>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "rgba(245,241,234,0.5)" }}>Admin</span>
        <button onClick={() => { sessionStorage.removeItem("leasly_admin_token"); setAdminToken(""); }}
          style={{ background: "none", border: "1px solid rgba(245,241,234,0.2)", color: "rgba(245,241,234,0.5)", padding: "4px 12px", borderRadius: 50, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
          Lock
        </button>
      </div>

      {/* ── Sidebar ── */}
      <div style={{ position: "fixed", left: 0, top: 54, bottom: 0, width: sidebarOpen ? 200 : 0, background: "#fff", borderRight: "1px solid #E8E4DC", overflow: "hidden", transition: "width 0.2s ease", zIndex: 35 }}>
        <div style={{ width: 200, paddingTop: 12, paddingBottom: 20 }}>
          {groups.map(g => (
            <div key={g}>
              <div style={{ padding: "8px 16px 4px", fontSize: 9, fontWeight: 700, color: "#B0ACA6", textTransform: "uppercase", letterSpacing: "0.12em" }}>{g}</div>
              {SECTIONS.filter(s => s.group === g).map(s => (
                <button key={s.id} onClick={() => navigateSection(s.id)}
                  style={{ display: "block", width: "100%", padding: "8px 16px", background: "none", border: "none", textAlign: "left", fontSize: 13, fontFamily: "inherit", cursor: "pointer", color: section === s.id ? "#163C2F" : "#2B2B2B", fontWeight: section === s.id ? 700 : 500, background: section === s.id ? "#EAF2ED" : "transparent", borderLeft: `3px solid ${section === s.id ? "#163C2F" : "transparent"}`, transition: "all 0.1s" }}>
                  {s.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{ marginTop: 54, marginLeft: sidebarOpen ? 200 : 0, padding: "28px 32px", transition: "margin-left 0.2s ease", minHeight: "calc(100vh - 54px)" }}>

        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#9B9690", marginBottom: 20 }}>
            <div style={{ width: 16, height: 16, border: "2px solid #E8E4DC", borderTop: "2px solid #163C2F", borderRadius: "50%", animation: "spin 0.8s linear infinite" }}/> Loading…
          </div>
        )}
        {error && <div style={{ background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 10, padding: "12px 16px", color: "#dc2626", fontSize: 13, marginBottom: 20 }}>{error}</div>}

        {/* ── DASHBOARD / USERS ── */}
        {/* Note: no `&& data` — we render the shell immediately so pressing back
            never shows a blank page; inner content handles null data gracefully */}
        {(section === "dashboard" || section === "users") && !viewUser && (
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: "#163C2F", marginBottom: 24 }}>
              {section === "dashboard" ? "Dashboard" : "Users"}
            </h1>
            {section === "dashboard" && data?.stats && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14, marginBottom: 28 }}>
                <StatCard label="Total Landlords"  value={data.stats.totalLandlords ?? "—"}  sub="Active accounts"/>
                <StatCard label="Total Properties" value={data.stats.totalProperties ?? "—"} sub="Across all landlords"/>
                <StatCard label="Total Tenants"    value={data.stats.totalTenants ?? "—"}    sub="All tenants"/>
                <StatCard label="Est. MRR"         value={fmtMoney(data.stats.estimatedMRR)} sub="Subscriptions + exports"/>
                <StatCard label="Export Revenue"   value={fmtMoney(data.stats.exportRevenue ?? 0)} sub="Report downloads"/>
              </div>
            )}
            <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: "16px 18px", borderBottom: "1px solid #E8E4DC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: "#163C2F" }}>Landlord Accounts</div>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…"
                  style={{ padding: "7px 12px", fontSize: 13, border: "1px solid #D4CFC7", borderRadius: 8, outline: "none", fontFamily: "inherit", width: 220 }}/>
              </div>
              {!data && !loading && (
                <EmptyState icon="🏠" title="No data" sub="Could not load accounts."/>
              )}
              {data?.landlords?.length === 0 && (
                <EmptyState icon="🏠" title="No landlord accounts" sub="No accounts found."/>
              )}
              {data?.landlords?.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>{["Account", "Email", "Tier", "Properties", "Tenants", "Last Login", ""].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {(data.landlords)
                        .filter(l => !search || l.name.toLowerCase().includes(search.toLowerCase()) || l.email.toLowerCase().includes(search.toLowerCase()))
                        .map(l => (
                          <tr key={l.id} style={{ borderBottom: "1px solid #F5F1EA" }}>
                            <td style={tdStyle}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#EAF2ED", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cormorant Garamond', serif", fontSize: 15, fontWeight: 700, color: "#163C2F", flexShrink: 0 }}>{initials(l.name)}</div>
                                <span style={{ fontWeight: 600, color: "#2B2B2B" }}>{l.name}</span>
                              </div>
                            </td>
                            <td style={{ ...tdStyle, color: "#9B9690" }}>{l.email}</td>
                            <td style={tdStyle}><Badge label={TIER_LABELS[l.tier] || l.tier} color={TIER_COLORS[l.tier] || "#9B9690"}/></td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>{l.propertyCount}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>{l.tenantCount}</td>
                            <td style={{ ...tdStyle, color: "#9B9690", fontSize: 12 }}>{fmtDate(l.lastLogin)}</td>
                            <td style={tdStyle}>
                              <button onClick={async () => {
                                try { const d = await callAdmin("landlord-detail", { userId: l.id }); setViewUser({ ...l, detail: d }); } catch(e) { setError(e.message); }
                              }} style={{ background: "none", border: "1px solid #D4CFC7", borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", color: "#163C2F", fontWeight: 600 }}>
                                View →
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── USER DETAIL ── */}
        {viewUser && (
          <div>
            <button onClick={() => setViewUser(null)} style={{ background: "none", border: "1px solid #D4CFC7", borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", color: "#163C2F", fontWeight: 600, marginBottom: 20 }}>← Back</button>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#EAF2ED", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: "#163C2F" }}>{initials(viewUser.name)}</div>
              <div>
                <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, fontWeight: 400, color: "#163C2F", margin: 0 }}>{viewUser.name}</h1>
                <div style={{ fontSize: 13, color: "#9B9690" }}>{viewUser.email}</div>
              </div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                <Badge label={TIER_LABELS[viewUser.tier] || viewUser.tier} color={TIER_COLORS[viewUser.tier] || "#9B9690"}/>
                <select value={viewUser.tier} onChange={async e => {
                  const t = e.target.value;
                  try { await callAdmin("update-tier", { userId: viewUser.id, tier: t }); setViewUser(u => ({ ...u, tier: t })); loadSection(section); } catch(err) { setError(err.message); }
                }} style={{ padding: "5px 10px", fontSize: 12, border: "1px solid #D4CFC7", borderRadius: 6, outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
                  <option value="free">Free</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="owner">Owner ✦</option>
                </select>
                <button
                  onClick={async () => {
                    const typed = window.prompt(`This permanently deletes the account's login access. Property/tenant data is preserved in the database.\n\nType the account's exact email to confirm:\n${viewUser.email}`);
                    if (typed === null) return;
                    if (typed.trim().toLowerCase() !== viewUser.email.trim().toLowerCase()) {
                      setError("Email did not match — deletion canceled.");
                      return;
                    }
                    try {
                      await callAdmin("delete-user", { userId: viewUser.id, email: typed.trim() });
                      setViewUser(null);
                      loadSection(section);
                    } catch(err) { setError(err.message); }
                  }}
                  style={{ padding: "5px 14px", fontSize: 12, border: "1.5px solid #dc2626", borderRadius: 6, background: "transparent", color: "#dc2626", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}
                >
                  Delete Account
                </button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
              <StatCard label="Properties"   value={viewUser.propertyCount} sub=""/>
              <StatCard label="Tenants"      value={viewUser.tenantCount}   sub=""/>
              <StatCard label="Plan MRR" value={fmtMoney(viewUser.mrr)} sub={viewUser.tier === "free" ? "Free plan" : `${viewUser.tier} plan`}/>
            </div>
            {viewUser.detail && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, padding: "18px 20px" }}>
                  <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17, color: "#163C2F", marginBottom: 14 }}>Properties ({viewUser.detail.properties?.length || 0})</div>
                  {viewUser.detail.properties?.length === 0 ? <div style={{ color: "#9B9690", fontSize: 13 }}>None</div>
                    : viewUser.detail.properties?.map((p, i) => <div key={i} style={{ fontSize: 13, padding: "6px 0", borderBottom: "1px solid #F5F1EA" }}>{p.address}</div>)}
                </div>
                <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, padding: "18px 20px" }}>
                  <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17, color: "#163C2F", marginBottom: 14 }}>Tenants ({viewUser.detail.tenants?.length || 0})</div>
                  {viewUser.detail.tenants?.length === 0 ? <div style={{ color: "#9B9690", fontSize: 13 }}>None</div>
                    : viewUser.detail.tenants?.slice(0, 8).map((t, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0", borderBottom: "1px solid #F5F1EA" }}>
                        <span>{t.name}</span>
                        <span style={{ color: "#9B9690" }}>{t.rent ? fmtMoney(t.rent) + "/mo" : "—"}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            <UserNotes userId={viewUser.id} callAdmin={callAdmin} />
          </div>
        )}

        {/* ── PROPERTIES ── */}
        {section === "properties" && data && !viewUser && (
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: "#163C2F", marginBottom: 24 }}>Properties</h1>
            <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #E8E4DC", fontSize: 13, color: "#9B9690" }}>
                {data.stats?.totalProperties || 0} properties across {data.stats?.totalLandlords || 0} landlords
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["Landlord", "Property Count", "Tenant Count", "Est. MRR"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                <tbody>
                  {(data.landlords || []).filter(l => l.propertyCount > 0).sort((a, b) => b.propertyCount - a.propertyCount).map(l => (
                    <tr key={l.id} style={{ borderBottom: "1px solid #F5F1EA" }}>
                      <td style={tdStyle}><span style={{ fontWeight: 600 }}>{l.name}</span><br/><span style={{ fontSize: 11, color: "#9B9690" }}>{l.email}</span></td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{l.propertyCount}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{l.tenantCount}</td>
                      <td style={tdStyle}>{fmtMoney(l.mrr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SUBSCRIPTIONS (read-only — nothing here writes to Stripe) ── */}
        {section === "subscriptions" && (() => {
          const rows = data?.rows || [];
          const pastDueRows = rows.filter(r => r.status === "past_due");
          const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
          const trialSoonRows = rows
            .filter(r => r.trialEnd && new Date(r.trialEnd).getTime() - Date.now() <= sevenDaysMs && new Date(r.trialEnd).getTime() > Date.now())
            .sort((a, b) => new Date(a.trialEnd) - new Date(b.trialEnd));
          const filteredRows = rows.filter(r =>
            (!subStatusFilter || r.status === subStatusFilter) &&
            (!search || r.name.toLowerCase().includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase()))
          );

          return (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: "#163C2F", margin: 0 }}>Subscriptions</h1>
                {data?.stripeMode && (
                  <Badge
                    label={`Stripe: ${data.stripeMode === "live" ? "Live mode" : data.stripeMode === "test" ? "Test mode" : "Unknown mode"}`}
                    color={data.stripeMode === "live" ? "#163C2F" : data.stripeMode === "test" ? "#B08D57" : "#9B9690"}
                  />
                )}
              </div>
              <div style={{ fontSize: 13, color: "#9B9690", marginBottom: 24 }}>Read-only view of live Stripe status. Nothing on this tab can change billing.</div>

              {data && (
                <>
                  {/* Summary cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 14, marginBottom: 24 }}>
                    <StatCard label="Trialing"       value={data.summary?.trialing ?? 0} />
                    <StatCard label="Active"         value={data.summary?.active ?? 0}   sub="Paying" />
                    <StatCard label="Canceling Soon" value={data.summary?.canceling ?? 0} />
                    <StatCard label="Past Due"       value={data.summary?.pastDue ?? 0} />
                    <StatCard label="Canceled"       value={data.summary?.canceled ?? 0} />
                    <StatCard label="Est. MRR"       value={fmtMoney(data.summary?.estimatedMRR)} sub="Active + canceling" />
                  </div>

                  {/* Failed payments */}
                  {pastDueRows.length > 0 && (
                    <div style={{ background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
                      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17, color: "#7f1d1d", marginBottom: 12 }}>⚠ Failed Payments</div>
                      {pastDueRows.map(r => (
                        <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, padding: "9px 0", borderBottom: "1px solid #fca5a544" }}>
                          <div>
                            <span style={{ fontWeight: 700, color: "#2B2B2B" }}>{r.name}</span>
                            <span style={{ color: "#9B9690", marginLeft: 8, fontSize: 12 }}>{r.email}</span>
                          </div>
                          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                            <span style={{ color: "#dc2626", fontWeight: 700, fontSize: 13 }}>{fmtMoney(r.pastDueAmount)}</span>
                            <span style={{ fontSize: 12, color: "#9B9690" }}>{r.pastDueDays ?? "—"} day{r.pastDueDays === 1 ? "" : "s"} overdue</span>
                            <a href={`mailto:${r.email}?subject=${encodeURIComponent("Your Leasly payment needs attention")}`}
                              style={{ fontSize: 12, color: "#163C2F", fontWeight: 700, textDecoration: "none" }}>
                              ✉ Email
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Trials ending soon */}
                  {trialSoonRows.length > 0 && (
                    <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
                      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17, color: "#163C2F", marginBottom: 12 }}>⏳ Trials Ending Soon</div>
                      {trialSoonRows.map(r => (
                        <div key={r.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F5F1EA" }}>
                          <div><span style={{ fontWeight: 600 }}>{r.name}</span><span style={{ color: "#9B9690", fontSize: 12, marginLeft: 8 }}>{r.email}</span></div>
                          <span style={{ color: "#B08D57", fontWeight: 700, fontSize: 13 }}>{fmtDate(r.trialEnd)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Customer table */}
                  <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ padding: "16px 18px", borderBottom: "1px solid #E8E4DC", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: "#163C2F" }}>Customers</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <select value={subStatusFilter} onChange={e => setSubStatusFilter(e.target.value)}
                          style={{ padding: "7px 10px", fontSize: 13, border: "1px solid #D4CFC7", borderRadius: 8, outline: "none", fontFamily: "inherit" }}>
                          <option value="">All statuses</option>
                          {Object.entries(SUB_STATUS_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…"
                          style={{ padding: "7px 12px", fontSize: 13, border: "1px solid #D4CFC7", borderRadius: 8, outline: "none", fontFamily: "inherit", width: 200 }}/>
                      </div>
                    </div>
                    {filteredRows.length === 0
                      ? <EmptyState icon="💳" title="No matching customers" sub="Try a different search or status filter."/>
                      : (
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>{["Name", "Email", "Tier", "Status", "Trial Ends", "Next Date", "Stripe"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {filteredRows.map(r => (
                                <tr key={r.id} style={{ borderBottom: "1px solid #F5F1EA" }}>
                                  <td style={tdStyle}>
                                    <span style={{ fontWeight: 600 }}>{r.name}</span>
                                    {r.tierMismatch && <span style={{ marginLeft: 8 }}><Badge label="⚠ Tier mismatch" color="#dc2626"/></span>}
                                  </td>
                                  <td style={{ ...tdStyle, color: "#9B9690" }}>{r.email}</td>
                                  <td style={tdStyle}><Badge label={TIER_LABELS[r.tier] || r.tier} color={TIER_COLORS[r.tier] || "#9B9690"}/></td>
                                  <td style={tdStyle}><Badge label={SUB_STATUS_LABELS[r.status] || r.status} color={SUB_STATUS_COLORS[r.status] || "#9B9690"}/></td>
                                  <td style={{ ...tdStyle, color: "#9B9690", fontSize: 12 }}>{fmtDate(r.trialEnd)}</td>
                                  <td style={{ ...tdStyle, color: "#9B9690", fontSize: 12 }}>{fmtDate(r.nextDate)}{r.cancelAtPeriodEnd ? " (cancels)" : ""}</td>
                                  <td style={tdStyle}>
                                    {r.stripeUrl
                                      ? <a href={r.stripeUrl} target="_blank" rel="noreferrer" style={{ color: "#163C2F", fontWeight: 600, fontSize: 12, textDecoration: "none" }}>Open →</a>
                                      : <span style={{ color: "#9B9690" }}>—</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ── WAITLIST ── */}
        {section === "waitlist" && (
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: "#163C2F", marginBottom: 24 }}>Waitlist</h1>
            {!data
              ? null /* loading handled by the top spinner */
              : data.error
                ? <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12 }}><EmptyState icon="📋" title="Table not found" sub={`${data.error} — run the SQL migration to create the waitlist table.`}/></div>
              : (data.waitlist ?? []).length === 0
                ? <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12 }}><EmptyState icon="📋" title="No signups yet" sub="Waitlist signups from the coming-soon page will appear here."/></div>
                : (
                  <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ padding: "14px 18px", borderBottom: "1px solid #E8E4DC", fontSize: 13, color: "#9B9690" }}>{data.waitlist.length} signups</div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr>{["Email", "Signed Up", "Source"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                      <tbody>
                        {data.waitlist.map((w, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #F5F1EA" }}>
                            <td style={tdStyle}>{w.email}</td>
                            <td style={{ ...tdStyle, color: "#9B9690" }}>{fmtDate(w.created_at)}</td>
                            <td style={{ ...tdStyle, color: "#9B9690" }}>{w.source || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
            }
          </div>
        )}

        {/* ── IDEAS ── */}
        {section === "ideas" && (
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: "#163C2F", marginBottom: 24 }}>Ideas & Feedback</h1>
            {!data
              ? null /* loading handled by the top spinner */
              : data.error
                ? <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12 }}><EmptyState icon="💡" title="Table not found" sub={`${data.error} — run the SQL migration to create the ideas table.`}/></div>
              : (data.ideas ?? []).length === 0
                ? <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12 }}><EmptyState icon="💡" title="No ideas submitted yet" sub="When visitors submit an idea via the landing page it will appear here."/></div>
                : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {data.ideas.map((idea, i) => (
                      <div key={i} style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, padding: "18px 20px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                          <div>
                            <span style={{ fontWeight: 700, fontSize: 14, color: "#2B2B2B" }}>{idea.name || "Anonymous"}</span>
                            <span style={{ fontSize: 12, color: "#9B9690", marginLeft: 10 }}>{idea.email}</span>
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ fontSize: 12, color: "#9B9690" }}>{fmtDate(idea.created_at)}</span>
                            {idea.status && <Badge label={idea.status} color={idea.status === "reviewed" ? "#7FB98E" : "#9B9690"}/>}
                          </div>
                        </div>
                        <p style={{ fontSize: 14, color: "#2B2B2B", lineHeight: 1.65, margin: "0 0 14px" }}>{idea.message}</p>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={async () => { try { await callAdmin("update-idea", { id: idea.id, status: "reviewed" }); loadSection("ideas"); } catch(e) { setError(e.message); } }}
                            style={{ padding: "5px 14px", fontSize: 12, border: "1px solid #7FB98E", background: "#EAF2ED", color: "#163C2F", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                            ✓ Mark Reviewed
                          </button>
                          <button onClick={async () => { try { await callAdmin("update-idea", { id: idea.id, status: "dismissed" }); loadSection("ideas"); } catch(e) { setError(e.message); } }}
                            style={{ padding: "5px 14px", fontSize: 12, border: "1px solid #D4CFC7", background: "none", color: "#9B9690", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>
                            Dismiss
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
            }
          </div>
        )}

        {/* ── REPORT EXPORTS ── */}
        {section === "report-exports" && (
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: "#163C2F", marginBottom: 8 }}>Report Exports</h1>
            {data?.totalRevenue !== undefined && (
              <div style={{ fontSize: 13, color: "#9B9690", marginBottom: 24 }}>
                Total revenue from report exports: <strong style={{ color: "#163C2F" }}>${(data.totalRevenue || 0).toFixed(2)}</strong>
              </div>
            )}
            {!data?.exports || data.exports.length === 0
              ? <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12 }}><EmptyState icon="📊" title="No exports yet" sub="Report exports will appear here once landlords start downloading."/></div>
              : (
                <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>{["Landlord", "Email", "Report", "Amount Paid", "Date"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {data.exports.map((e, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #F5F1EA" }}>
                          <td style={tdStyle}>{e.landlord}</td>
                          <td style={{ ...tdStyle, color: "#9B9690" }}>{e.email}</td>
                          <td style={tdStyle}>{e.report_type?.replace(/_/g, " ")}</td>
                          <td style={{ ...tdStyle, fontWeight: 600, color: e.amount_paid > 0 ? "#163C2F" : "#9B9690" }}>{e.amount_paid_display}</td>
                          <td style={{ ...tdStyle, color: "#9B9690", fontSize: 12 }}>{fmtDate(e.exported_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        )}

        {/* ── ERROR LOG ── */}
        {section === "error-log" && (
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: "#163C2F", marginBottom: 24 }}>Error Log</h1>
            {!data?.errors || data.errors.length === 0
              ? <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12 }}><EmptyState icon="✅" title="No errors logged" sub="Platform errors from edge functions will appear here." /></div>
              : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {data.errors.map(e => (
                    <div key={e.id} style={{ background: "#fff", border: `1px solid ${e.resolved ? "#C5DBC9" : "#fca5a544"}`, borderRadius: 12, padding: "16px 20px", opacity: e.resolved ? 0.6 : 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <Badge label={e.error_type} color={e.error_type.includes("webhook") ? "#dc2626" : e.error_type.includes("email") ? "#B08D57" : "#9B9690"} />
                          <span style={{ fontSize: 12, color: "#9B9690", fontFamily: "'Manrope', sans-serif" }}>{e.source}</span>
                          {e.resolved && <Badge label="Resolved" color="#7FB98E" />}
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                          <span style={{ fontSize: 11, color: "#9B9690" }}>{fmtDate(e.created_at)}</span>
                          {!e.resolved && (
                            <button onClick={async () => {
                              try { await callAdmin("resolve-error", { errorId: e.id }); loadSection("error-log"); } catch(err) { setError(err.message); }
                            }} style={{ padding: "4px 12px", fontSize: 11, border: "1px solid #C5DBC9", background: "#EAF2ED", color: "#163C2F", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                              ✓ Resolve
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: "#2B2B2B", lineHeight: 1.6, marginBottom: 6 }}>{e.message}</div>
                      {e.user_id && <div style={{ fontSize: 11, color: "#9B9690" }}>User: {e.user_id}</div>}
                      {e.metadata && <div style={{ fontSize: 11, color: "#9B9690", marginTop: 2 }}>Details: {JSON.stringify(e.metadata)}</div>}
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* ── HEALTH ── */}
        {section === "health" && data && (
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: "#163C2F", marginBottom: 24 }}>Platform Health</h1>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14, marginBottom: 24 }}>
              {(data.tables || []).map(t => (
                <div key={t.name} style={{ background: "#fff", border: `1px solid ${t.ok ? "#C5DBC9" : "#fca5a5"}`, borderRadius: 12, padding: "18px 20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9B9690" }}>{t.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: t.ok ? "#22c55e" : "#dc2626" }}>{t.ok ? "✓ OK" : "✗ Error"}</span>
                  </div>
                  <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, color: "#163C2F" }}>{t.count?.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: "#9B9690", marginTop: 4 }}>rows</div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, padding: "16px 20px", fontSize: 12, color: "#9B9690" }}>
              Last checked: {fmtDate(data.timestamp)} · Supabase project: cweuzllrmfwbfhnuqpbc
            </div>
          </div>
        )}

        {/* ── ACTIVITY (audit log) ── */}
        {section === "activity" && (
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: "#163C2F", marginBottom: 8 }}>Activity</h1>
            <div style={{ fontSize: 13, color: "#9B9690", marginBottom: 24 }}>Latest 100 admin actions and login attempts, newest first.</div>
            {!data?.entries || data.entries.length === 0
              ? <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12 }}><EmptyState icon="🗒️" title="No activity logged yet" sub="Admin actions and login attempts will appear here."/></div>
              : (
                <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, overflow: "hidden" }}>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr>{["Time", "Action", "Target", "IP", "Result"].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                      <tbody>
                        {data.entries.map(e => (
                          <tr key={e.id} style={{ borderBottom: "1px solid #F5F1EA" }}>
                            <td style={{ ...tdStyle, color: "#9B9690", fontSize: 12, whiteSpace: "nowrap" }}>{fmtDateTime(e.created_at)}</td>
                            <td style={tdStyle}><span style={{ fontWeight: 600 }}>{e.action}</span></td>
                            <td style={{ ...tdStyle, color: "#9B9690", fontFamily: "monospace", fontSize: 12 }}>{e.target || "—"}</td>
                            <td style={{ ...tdStyle, color: "#9B9690", fontFamily: "monospace", fontSize: 12 }}>{e.ip || "—"}</td>
                            <td style={tdStyle}>
                              <Badge label={e.success ? "Success" : "Failed"} color={e.success ? "#7FB98E" : "#dc2626"}/>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
}
